import { useAtom, useStore } from "jotai";
import { DateTime } from "luxon";
import { useCallback, useEffect, useRef, useState } from "react";

import type { EventFormValues } from "@/types/event";

import { eventFamily } from "@/atoms/event";
import {
  eventFormFamily,
  eventRawFamily,
  eventTicketKindIdFamily,
} from "@/atoms/event-form";
import Bugsnag from "@/utils/bugsnag";
import { toDisplayEvent } from "@/utils/event-helpers";
import { updateEvent, upsertTicketKind } from "@/utils/event-queries";
import { formatDescription } from "@/utils/formatStrings";

const DEBOUNCE_MS = 1200;

const IMMEDIATE_KEYS: ReadonlySet<string> = new Set(["status", "visibility"]);

// ── Pure helpers ────────────────────────────────────────────────────

export interface AutoSave {
  error: null | string;
  patch: (partial: Partial<EventFormValues>) => void;
  saving: boolean;
  values: EventFormValues | null;
}

export function useEventAutoSave(
  slug: string,
  eventId: null | string,
): AutoSave {
  const store = useStore();
  const [formValues, setFormValues] = useAtom(eventFormFamily(slug));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<null | string>(null);

  const timerRef = useRef<null | ReturnType<typeof setTimeout>>(null);
  const persistingRef = useRef(false);
  const queuedRef = useRef(false);

  // Read latest atoms without stale closures
  const persist = useCallback(async () => {
    const currentId = eventId;
    if (!currentId) return;

    const currentForm = store.get(eventFormFamily(slug));
    const raw = store.get(eventRawFamily(slug));
    const ticketKindId = store.get(eventTicketKindIdFamily(slug));
    if (!currentForm) return;

    // Don't overlap — queue instead
    if (persistingRef.current) {
      queuedRef.current = true;
      return;
    }
    persistingRef.current = true;
    setSaving(true);

    try {
      const fullEventData = formValuesToEventColumns(currentForm);

      // Diff against last-known DB state
      const changedEventData: Record<string, unknown> = {};
      if (raw) {
        for (const [key, value] of Object.entries(fullEventData)) {
          if (JSON.stringify(value) !== JSON.stringify(raw[key])) {
            changedEventData[key] = value;
          }
        }
      } else {
        Object.assign(changedEventData, fullEventData);
      }

      // Persist event columns
      if (Object.keys(changedEventData).length > 0) {
        const ok = await updateEvent(currentId, changedEventData);
        if (!ok) throw new Error("Failed to update event");
      }

      // Persist ticket if ticket fields changed
      const ticketData = formValuesToTicketData(currentForm);
      const originalTier = (
        raw?.event_ticket_kind as Record<string, unknown>[] | undefined
      )?.[0];

      const ticketChanged =
        (ticketData.price_cents > 0 || currentForm.ticket_name) &&
        (!originalTier ||
          ticketData.currency !== originalTier.currency ||
          ticketData.description !== (originalTier.description ?? undefined) ||
          ticketData.name !== originalTier.name ||
          ticketData.price_cents !== originalTier.price_cents ||
          ticketData.quantity_total !== originalTier.quantity_total);

      if (ticketChanged) {
        await upsertTicketKind(currentId, ticketData, ticketKindId);
      }

      // Update raw state to reflect what's now in the DB
      const mergedRaw = raw ? { ...raw, ...fullEventData } : fullEventData;
      store.set(eventRawFamily(slug), mergedRaw);

      // Keep DisplayEvent atom in sync
      store.set(
        eventFamily(slug),
        toDisplayEvent(mergedRaw as Parameters<typeof toDisplayEvent>[0]),
      );

      setError(null);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Failed to save";
      Bugsnag.notify(e instanceof Error ? e : new Error(String(e)));
      setError(msg);
    } finally {
      persistingRef.current = false;
      setSaving(false);

      // If another persist was queued while we were busy, run it now
      if (queuedRef.current) {
        queuedRef.current = false;
        void persist();
      }
    }
  }, [eventId, slug, store]);

  const patch = useCallback(
    (partial: Partial<EventFormValues>) => {
      // 1. Update form atom immediately
      setFormValues((prev) => (prev ? { ...prev, ...partial } : null));

      // 2. Update DisplayEvent atom for view-mode consumers
      const raw = store.get(eventRawFamily(slug));
      const currentForm = store.get(eventFormFamily(slug));
      if (raw && currentForm) {
        const merged = { ...currentForm, ...partial };
        const columns = formValuesToEventColumns(merged);
        const mergedRaw = { ...raw, ...columns };
        store.set(
          eventFamily(slug),
          toDisplayEvent(mergedRaw as Parameters<typeof toDisplayEvent>[0]),
        );
      }

      // 3. Schedule persist
      if (timerRef.current) clearTimeout(timerRef.current);

      const hasImmediate = Object.keys(partial).some((k) =>
        IMMEDIATE_KEYS.has(k),
      );
      timerRef.current = setTimeout(
        () => {
          timerRef.current = null;
          void persist();
        },
        hasImmediate ? 0 : DEBOUNCE_MS,
      );
    },
    [setFormValues, store, slug, persist],
  );

  // Flush pending save on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
        void persist();
      }
    };
  }, [persist]);

  return { error, patch, saving, values: formValues };
}

// ── Hook ────────────────────────────────────────────────────────────

function formValuesToEventColumns(
  values: EventFormValues,
): Record<string, unknown> {
  const locationGeo =
    values.location_lat && values.location_lng
      ? `POINT(${values.location_lng} ${values.location_lat})`
      : null;

  return {
    description: formatDescription(values.description),
    ends_at: (() => {
      const start = DateTime.fromISO(values.starts_at);
      if (!start.isValid) return null;
      const dur = parseInt(values.duration_minutes, 10) || 120;
      return start.plus({ minutes: dur }).toISO()!;
    })(),
    flyer_url: values.flyer_url || null,
    location_city: values.location_city,
    location_country: values.location_country,
    location_geo: locationGeo,
    location_name: values.location_name,
    location_neighborhood: values.location_neighborhood || null,
    location_place_id: values.location_place_id || null,
    location_postal_code: values.location_postal_code,
    location_state: values.location_state,
    location_street: values.location_street,
    starts_at: values.starts_at || null,
    status: values.status,
    timezone: values.timezone,
    title: formatDescription(values.title),
    visibility: values.visibility,
  };
}

function formValuesToTicketData(values: EventFormValues) {
  return {
    currency: values.currency || "usd",
    description: values.ticket_description || undefined,
    name: values.ticket_name || "General Admission",
    price_cents: parseInt(values.price_cents, 10) || 0,
    quantity_total: parseInt(values.quantity_total, 10) || 10,
  };
}
